/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

export type QueryStatsTemplateCategory = 'ADVERTISING' | 'PUBLISHING' | 'DATA' | 'OTHER';

export interface QueryStatsTypeDefinition {
  type: string;
  label: string;
  category: QueryStatsTemplateCategory;
  state: string;
}

export interface QueryStatsTemplateData {
  selectAllChildren: boolean;
  children: any[];
  parent: any[];
  superParent: any[];
  source: string;
  political: boolean | null;
  grouping: string | null;
  grouping2: string | null;
  includeAll: boolean;
  currency: string | null;
  bidCurrency: string | null;
  advObjectLabel: any[];
  pubObjectLabel: any[];
  dateOptionId: string | null;
  startDate: any;
  endDate: any;
}

export const QUERY_STATS_TYPES: QueryStatsTypeDefinition[] = [
  {type: 'ADVERTISER', label: 'Advertiser', category: 'ADVERTISING', state: 'app.rep.queries.advertiser'},
  {type: 'ORDER', label: 'Order', category: 'ADVERTISING', state: 'app.rep.queries.order'},
  {type: 'LINE_ITEM', label: 'Line Item', category: 'ADVERTISING', state: 'app.rep.queries.line-item'},
  {type: 'FOLIO', label: 'Folio', category: 'ADVERTISING', state: 'app.rep.queries.folio'},
  {type: 'CREATIVE_SET', label: 'Creative Set', category: 'ADVERTISING', state: 'app.rep.queries.creative-set'},
  {type: 'CREATIVE', label: 'Creative', category: 'ADVERTISING', state: 'app.rep.queries.creative'},
  {type: 'EARNINGS_ACCOUNT', label: 'Earnings Account', category: 'PUBLISHING', state: 'app.rep.queries.earnings-account'},
  {type: 'SITE', label: 'Site', category: 'PUBLISHING', state: 'app.rep.queries.site'},
  {type: 'AD_UNIT', label: 'Ad Unit', category: 'PUBLISHING', state: 'app.rep.queries.ad-unit'},
  {type: 'FOLDER', label: 'Folder', category: 'DATA', state: 'app.data-queries.folder'},
  {type: 'USER_SEGMENT', label: 'Targeted Advertising', category: 'DATA', state: 'app.data-queries.targeting'}
];

export function queryStatsTypeDefinition(type: string): QueryStatsTypeDefinition | undefined {
  return _.find(QUERY_STATS_TYPES, {type});
}

export function queryStatsCategory(type: string): QueryStatsTemplateCategory {
  return queryStatsTypeDefinition(type)?.category || 'OTHER';
}

export function queryStatsTypeLabel(type: string): string {
  return queryStatsTypeDefinition(type)?.label || _.startCase(_.toLower(type || 'Other'));
}

export function captureQueryStatsTemplateData(query: any): QueryStatsTemplateData {
  return {
    selectAllChildren: !!query?.selectAllChildren,
    children: cloneSelections(query?.children),
    parent: cloneSelections(query?.parent),
    superParent: cloneSelections(query?.superParent),
    source: query?.source || '',
    political: _.isBoolean(query?.political) ? query.political : null,
    grouping: groupingKey(query?.grouping),
    grouping2: groupingKey(query?.grouping2),
    includeAll: !!query?.includeAll,
    currency: query?.currency || null,
    bidCurrency: query?.bidCurrency || null,
    advObjectLabel: _.cloneDeep(query?.advObjectLabel || []),
    pubObjectLabel: _.cloneDeep(query?.pubObjectLabel || []),
    dateOptionId: query?.dateOption?.id || null,
    startDate: serializableDate(query?.startDate),
    endDate: serializableDate(query?.endDate)
  };
}

export function applyQueryStatsTemplateData(query: any, data: QueryStatsTemplateData): void {
  if (!query || !data) {
    return;
  }

  query.selectAllChildren = !!data.selectAllChildren;
  query.children = _.cloneDeep(data.children || []);
  query.parent = _.cloneDeep(data.parent || []);
  query.superParent = _.cloneDeep(data.superParent || []);
  query.source = data.source || '';
  query.political = _.isBoolean(data.political) ? data.political : null;
  query.grouping = findGrouping(query.groupingSelections, data.grouping) || query.grouping;
  query.grouping2 = findGrouping(query.subGroupingSelections, data.grouping2) || '';
  query.includeAll = !!data.includeAll;
  query.currency = data.currency || query.currency;
  query.bidCurrency = data.bidCurrency || null;
  query.advObjectLabel = _.cloneDeep(data.advObjectLabel || []);
  query.pubObjectLabel = _.cloneDeep(data.pubObjectLabel || []);

  if ((data.dateOptionId || data.startDate || data.endDate) && _.isFunction(query.changeCallback)) {
    query.changeCallback(data.dateOptionId || undefined, data.startDate, data.endDate);
  }
}

function cloneSelections(selections: any[]): any[] {
  return _.map(selections || [], (selection: any) => _.pick(selection, [
    'id',
    'name',
    'url',
    'objectState',
    'source',
    'type'
  ]));
}

function groupingKey(grouping: any): string | null {
  if (!grouping) {
    return null;
  }
  return _.isString(grouping) ? grouping : grouping.caps || grouping.type || grouping.apiParam || null;
}

function findGrouping(selections: any[], key: string | null): any {
  if (!key) {
    return null;
  }
  return _.get(_.find(selections || [], (selection: any) => groupingKey(selection.val) === key), 'val');
}

function serializableDate(value: any): any {
  if (_.isNil(value)) {
    return null;
  }
  if (_.isFunction(value.toISOString)) {
    return value.toISOString();
  }
  if (_.isFunction(value.toDate)) {
    return value.toDate().toISOString();
  }
  return value;
}
